import {SetStateAction} from "react";
import {ColumnId, ExtractTableItemKey, TableConfig} from "./config";
import {Type} from "../../utils/typing";
import {typedEntries, typedKeys} from "../../utils/typescript";
import {getRenderableColumnIds, isColumnGroupable, isColumnRenderable, isColumnSortable} from "./utility";
import {OrderClause, OrderDirection} from "platformed-client/types";

// State types
// These are not derived from schemas.

export type GroupState<C extends TableConfig = any> = readonly GroupStateEntry<C>[];

export type GroupStateEntry<C extends TableConfig> = {
  readonly columnId: ColumnId<C>;
  readonly direction: OrderDirection;
};

export type SortState<C extends TableConfig> = {
  readonly columnId: ColumnId<C>;
  readonly direction: OrderDirection;
} | null;

export type PersistedTableState<C extends TableConfig = any> = {
  columnVisibility: Record<ColumnId<C>, boolean>;
  columnOrder: ColumnId<C>[];
  groupState: GroupState<C>;
  sortState: SortState<C>;
};

export type SetColumnVisibleFunction<C extends TableConfig> = (
  columnId: ColumnId<C>,
  visible: SetStateAction<boolean>,
) => void;
export type SetGroupStateFunction<C extends TableConfig> = (action: SetStateAction<GroupState<C>>) => void;
export type SetSortStateFunction<C extends TableConfig> = (action: SetStateAction<SortState<C>>) => void;
export type SetColumnPositionFunction<C extends TableConfig> = (
  columnId: ColumnId<C>,
  position: SetStateAction<number>,
) => void;
export type SetSelectedRowKeysFunction<C extends TableConfig> = (
  selected: SetStateAction<Set<ExtractTableItemKey<C>>>,
) => void;
export type SetRowSelectedFunction<C extends TableConfig> = (
  key: ExtractTableItemKey<C>,
  selected: SetStateAction<boolean>,
) => void;
export type ResetFunction = () => void;
export type ClearSelectedRowsFunction = () => void;

export type TableState<C extends TableConfig = any> = {
  visibleColumns: Set<ColumnId<C>>;
  columnOrder: ColumnId<C>[];
  groupState: GroupState<C>;
  sortState: SortState<C>;

  isChangedFromDefault: boolean;

  setColumnVisible: SetColumnVisibleFunction<C>;
  setGroupState: SetGroupStateFunction<C>;
  setSortState: SetSortStateFunction<C>;
  setColumnPosition: SetColumnPositionFunction<C>;

  /** Reset all state to the defaults. */
  reset: ResetFunction;
};

// Schemas

const SORT_DIRECTION_SCHEMA = Type.Union([Type.Literal("ascending"), Type.Literal("descending")]);

function generateColumnVisibilitySchema<C extends TableConfig>(config: C) {
  const columnIds = getRenderableColumnIds(config);
  return Type.Record(Type.Union(columnIds.map(id => Type.Literal(id))), Type.Boolean());
}

function generateColumnOrderSchema<C extends TableConfig>(config: C) {
  const columnIds = typedEntries(config.columns)
    .filter(([_columnId, column]) => isColumnRenderable(column))
    .map(([columnId]) => columnId);

  return Type.Array(Type.Union(columnIds.map(id => Type.Literal(id))), {
    uniqueItems: true,
    minItems: columnIds.length,
    maxItems: columnIds.length,
  });
}

function generateGroupStateSchema<C extends TableConfig>(config: C) {
  const columnIds = typedKeys(config.columns);
  const groupableColumnIds = columnIds.filter(columnId => isColumnGroupable(config.columns[columnId]));

  return Type.Array(
    Type.Object({
      columnId: Type.Union(groupableColumnIds.map(id => Type.Literal(id))),
      direction: SORT_DIRECTION_SCHEMA,
    }),
  );
}

function generateSortStateSchema<C extends TableConfig>(config: C) {
  const columnIds = typedKeys(config.columns);
  const sortableColumnIds = columnIds.filter(columnId => isColumnSortable(config.columns[columnId]));

  return Type.Union([
    Type.Object({
      columnId: Type.Union(sortableColumnIds.map(id => Type.Literal(id))),
      direction: SORT_DIRECTION_SCHEMA,
    }),
    Type.Null(),
  ]);
}

export type PersistedStateSchema<C extends TableConfig> = ReturnType<typeof generatePersistedTableStateSchema<C>>;

export function generatePersistedTableStateSchema<C extends TableConfig>(config: C) {
  return Type.Object({
    columnVisibility: generateColumnVisibilitySchema(config),
    columnOrder: generateColumnOrderSchema(config),
    groupState: generateGroupStateSchema(config),
    sortState: generateSortStateSchema(config),
  });
}

export function encodeOrdering<C extends TableConfig>(config: C, state: TableState<C>): OrderClause[] {
  return [...state.groupState, state.sortState].flatMap(entry => {
    const backendFields = (entry?.columnId && config.columns[entry.columnId]?.backendFields) ?? [];
    return backendFields.map(field => ({field, direction: entry!.direction}));
  });
}
